import Link from 'next/link'
import GridListToggle from 'components/GridListToggle'
import ScrollableTabs from 'components/ScrollableTabs'
import { classNames } from 'helpers/utilities'

export type DashboardTab<TabId> = {
  name: string
  id: TabId
  href: string
  count: number
  isCurrent: boolean
  isDisabled?: boolean
}

export type ViewSetting = 'grid' | 'list'

export interface DashboardControlsProps<TabId> {
  tabs: Array<DashboardTab<TabId>>
  current: TabId
  viewSetting: ViewSetting
  setViewSetting: (view: ViewSetting) => void
  hideViewToggle?: Array<TabId>
}

export default function DashboardControls<TabId>({
  tabs,
  current,
  viewSetting,
  setViewSetting,
  hideViewToggle
}: DashboardControlsProps<TabId>) {
  const renderViewToggle = () => {
    const hideToggle = Array.isArray(hideViewToggle) && hideViewToggle.includes(current)

    return (
      <div className={classNames(hideToggle ? 'hidden' : 'hidden md:flex items-center')}>
        <GridListToggle onChange={setViewSetting} value={viewSetting} />
      </div>
    )
  }

  return (
    <div className="bg-white border-b border-gray-200 sm:px-6 lg:px-8 sticky top-16 z-20 dark:bg-gray-900 dark:border-gray-800">
      <ScrollableTabs>
        <div className="flex justify-between">
          <nav className="flex sm:space-x-8" aria-label="Tabs">
            {tabs.map((tab) =>
              !tab.isDisabled ? (
                <Link
                  key={tab.href}
                  href={tab.href}
                  className={classNames(
                    tab.isCurrent
                      ? 'border-brand-500 text-brand-600 dark:border-brand-600 dark:text-brand-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200 dark:hover:text-gray-300 dark:hover:border-gray-700',
                    'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm mx-4 sm:mx-auto'
                  )}
                >
                  {tab.name}
                  {tab.count ? (
                    <span
                      className={classNames(
                        tab.isCurrent
                          ? 'bg-brand-100 text-brand-600 dark:bg-brand-300/10 dark:text-brand-500'
                          : 'bg-gray-100 text-gray-900 dark:bg-gray-300/10 dark:text-gray-400',
                        'hidden ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block'
                      )}
                    >
                      {tab.count}
                    </span>
                  ) : null}
                </Link>
              ) : (
                <span
                  key={tab.name}
                  className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 opacity-50 pointer-events-none mx-4 sm:mx-auto"
                >
                  {tab.name}
                </span>
              )
            )}
          </nav>
          {renderViewToggle()}
        </div>
      </ScrollableTabs>
    </div>
  )
}
